import Image from 'next/image';
import { oneLine } from 'common-tags';

import { RichInline } from '@/atoms';

import type { FunctionComponent } from 'react';
import type { TypeContentListProps } from './types';

/**
 * Lista bajo el contenido de `ContenidoConImagen` (Figma SERVICIOS). Tres
 * variantes según `tipo`:
 *  - `checklist`  → filas icono(32px) + texto (admite marks inline), vertical.
 *  - `pills`      → pastillas con borde del color de marca, sin icono.
 *  - `etiquetas`  → pastillas rellenas (tinte ctacolor) con icono(16px) + `label`.
 */
export const ContentList: FunctionComponent<TypeContentListProps> = ({
  className = '',
  tipo = 'checklist',
  label,
  items,
}: TypeContentListProps) => {
  if (!items?.length) return null;

  if (tipo === 'checklist') {
    return (
      <ul className={oneLine`mt-6 flex w-full flex-col gap-4 ${className}`}>
        {items.map((item, index) => (
          <li key={index} className="flex items-start gap-2 px-4">
            {item.icon?.src && (
              <span className="relative mt-0.5 size-8 shrink-0">
                <Image
                  src={item.icon.src}
                  alt={item.icon.alt ?? ''}
                  fill
                  sizes="32px"
                  className="object-contain"
                />
              </span>
            )}
            <RichInline
              html={item.text}
              className="text-text-primary text-lg leading-7 lg:text-[22px]"
            />
          </li>
        ))}
      </ul>
    );
  }

  if (tipo === 'pills') {
    return (
      <ul className={oneLine`mt-6 flex flex-wrap gap-3 ${className}`}>
        {items.map((item, index) => (
          <li
            key={index}
            className="border-primary-500 text-primary-500 rounded-full border-2 px-5 py-2 text-base font-bold"
          >
            {item.text}
          </li>
        ))}
      </ul>
    );
  }

  // etiquetas
  return (
    <div className={oneLine`mt-6 flex flex-col gap-3 ${className}`}>
      {label && (
        <p className="text-text-primary font-primary text-2xl leading-8 font-bold">
          {label}
        </p>
      )}
      <ul className="flex flex-wrap gap-5">
        {items.map((item, index) => (
          <li
            key={index}
            className="text-secondary-500 flex items-center gap-2 rounded-full bg-[rgb(var(--tw-color-secondary-500)/0.1)] px-4 py-2 text-sm font-bold"
          >
            {item.icon?.src && (
              <span className="relative size-4 shrink-0">
                <Image
                  src={item.icon.src}
                  alt={item.icon.alt ?? ''}
                  fill
                  sizes="16px"
                  className="object-contain"
                />
              </span>
            )}
            {item.text}
          </li>
        ))}
      </ul>
    </div>
  );
};

ContentList.displayName = 'ContentList';
